import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Users, UserRound, Trophy, ClipboardList, Sparkles, Settings as SettingsIcon, LogOut, Dna, Route as RouteIcon, PenLine, FileText, Award, MessageCircle, GitCompare, CalendarRange, Flag, MoreHorizontal, MessageSquarePlus, X, Target, Shield, Megaphone, HelpCircle, Gift, Rocket, CreditCard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import GlobalSearch from "@/components/GlobalSearch";
import TrialBanner from "@/components/TrialBanner";
import FounderBanner from "@/components/FounderBanner";

const COACH = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/season", label: "Plan My Season", icon: CalendarRange, testid: "nav-season" },
  { to: "/goals", label: "Season Goals", icon: Target, testid: "nav-goals" },
  { to: "/game-day", label: "Game Day Mode", icon: Flag, testid: "nav-game-day" },
  { to: "/coach-dna", label: "Coach DNA", icon: Dna, testid: "nav-coach-dna" },
  { to: "/journey", label: "Journey", icon: RouteIcon, testid: "nav-journey" },
  { to: "/reflect", label: "Reflect", icon: PenLine, testid: "nav-reflect" },
  { to: "/monthly-report", label: "Monthly Report", icon: FileText, testid: "nav-monthly" },
  { to: "/legacy", label: "Legacy", icon: Award, testid: "nav-legacy" },
];
const TEAM = [
  { to: "/teams", label: "Teams", icon: Users, testid: "nav-teams" },
  { to: "/players", label: "Players", icon: UserRound, testid: "nav-players" },
  { to: "/games", label: "Games", icon: Trophy, testid: "nav-games" },
  { to: "/games/compare", label: "Compare Games", icon: GitCompare, testid: "nav-compare" },
  { to: "/practices", label: "Practices", icon: ClipboardList, testid: "nav-practices" },
  { to: "/coach-ai", label: "Coaching Partner", icon: MessageCircle, testid: "nav-coach-ai" },
];
// Bottom nav: Home / Season / Practice / Game Day / Partner
const BOTTOM = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard, testid: "nav-dashboard-mobile" },
  { to: "/season", label: "Season", icon: CalendarRange, testid: "nav-season-mobile" },
  { to: "/practices", label: "Practice", icon: ClipboardList, testid: "nav-practices-mobile" },
  { to: "/game-day", label: "Game Day", icon: Flag, testid: "nav-game-day-mobile" },
  { to: "/coach-ai", label: "Partner", icon: MessageCircle, testid: "nav-partner-mobile" },
];
const MORE_ITEMS = [
  { to: "/coach-dna", label: "Coach DNA", icon: Dna },
  { to: "/journey", label: "Journey", icon: RouteIcon },
  { to: "/goals", label: "Season Goals", icon: Target },
  { to: "/players", label: "Players", icon: UserRound },
  { to: "/teams", label: "Teams", icon: Users },
  { to: "/games", label: "Games", icon: Trophy },
  { to: "/games/compare", label: "Compare Games", icon: GitCompare },
  { to: "/reflect", label: "Reflect", icon: PenLine },
  { to: "/monthly-report", label: "Monthly Report", icon: FileText },
  { to: "/legacy", label: "Legacy", icon: Award },
  { to: "/referrals", label: "Refer coaches", icon: Gift },
  { to: "/beta", label: "Beta Program", icon: Rocket },
  { to: "/help", label: "Help Center", icon: HelpCircle },
  { to: "/settings/billing", label: "Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

function Item({ to, label, icon: Icon, testid, onClick }) {
  return (
    <NavLink to={to} data-testid={testid} onClick={onClick} className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium transition-colors ${
        isActive ? "bg-[#007AFF]/10 text-[#007AFF]" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
      }`
    }>
      <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
      {label}
    </NavLink>
  );
}

function FeedbackDialog({ open, onClose }) {
  const [kind, setKind] = useState("bug");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try { await api.post("/feedback", { kind, message: msg }); toast.success("Thanks — we read every note"); onClose(); setMsg(""); }
    catch { toast.error("Couldn't send"); }
    finally { setBusy(false); }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm p-4 flex items-end md:items-center justify-center animate-fade-in" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-5 md:p-6 shadow-xl" data-testid="feedback-dialog">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold text-lg">Send feedback</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-800 p-1" data-testid="feedback-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {[
            { k: "bug", label: "🐞 Bug" },
            { k: "feature", label: "✨ Feature" },
            { k: "confusing", label: "🤔 Confusing" },
            { k: "liked", label: "❤️ Loved it" },
          ].map(o => (
            <button key={o.k} data-testid={`fb-kind-${o.k}`} onClick={()=>setKind(o.k)} className={`px-3 py-1.5 rounded-full text-sm font-semibold ${kind === o.k ? "bg-[#007AFF] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>{o.label}</button>
          ))}
        </div>
        <textarea data-testid="feedback-message" value={msg} onChange={e=>setMsg(e.target.value)} rows="4" placeholder="Tell us what's on your mind…" className="w-full p-3 rounded-2xl border border-gray-200 focus:border-[#007AFF] focus:ring-2 focus:ring-[#007AFF]/20 outline-none resize-none bg-white text-sm" />
        <button data-testid="feedback-submit" onClick={submit} disabled={busy || !msg.trim()} className="mt-3 w-full bg-[#007AFF] hover:bg-[#0056B3] disabled:opacity-40 text-white font-semibold rounded-full py-2.5 transition-colors">{busy ? "Sending…" : "Send"}</button>
      </div>
    </div>
  );
}

function MoreSheet({ open, onClose, isAdmin }) {
  if (!open) return null;
  const items = isAdmin ? [...MORE_ITEMS, { to: "/admin", label: "Admin", icon: Shield }] : MORE_ITEMS;
  return (
    <div className="md:hidden fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-5 pb-8 max-h-[80vh] overflow-y-auto" data-testid="more-sheet">
        <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mb-4" />
        <div className="font-display font-bold text-lg mb-2">More</div>
        <div className="grid grid-cols-3 gap-2">
          {items.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} onClick={onClose} data-testid={`more-${to.slice(1)}`} className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-gray-50 hover:bg-gray-100 text-gray-800">
              <Icon className="w-5 h-5 text-[#007AFF]" />
              <span className="text-xs font-medium text-center leading-tight">{label}</span>
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [fbOpen, setFbOpen] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("coachiq_ann_dismissed") || null);

  useEffect(() => {
    api.get("/announcements/active").then(r => setAnnouncement(r.data)).catch(() => {});
  }, []);

  const dismiss = () => { if (announcement) { localStorage.setItem("coachiq_ann_dismissed", announcement.id); setDismissed(announcement.id); } };
  const showAnn = announcement && announcement.id !== dismissed;

  return (
    <div className="min-h-screen field-bg">
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-gray-100 fixed top-0 left-0 h-screen z-40">
        <div className="px-6 pt-8 pb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-[#007AFF] flex items-center justify-center shadow-md shadow-blue-500/20">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-display font-bold text-lg tracking-tight text-gray-900">CoachIQ</span>
                <span data-testid="beta-badge" className="text-[9px] font-bold uppercase tracking-widest text-white bg-gradient-to-br from-emerald-500 to-emerald-600 rounded px-1.5 py-0.5">Beta</span>
              </div>
              <div className="text-[11px] text-gray-500 -mt-0.5">by UPHAUS Labs · v0.9</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 px-3 pt-1 pb-2">Your growth</div>
          {COACH.map(item => <Item key={item.to} {...item} />)}
          <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 px-3 pt-4 pb-2">Your team</div>
          {TEAM.map(item => <Item key={item.to} {...item} />)}
          <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 px-3 pt-4 pb-2">Community</div>
          <Item to="/referrals" label="Refer coaches" icon={Gift} testid="nav-referrals" />
          <Item to="/beta" label="Beta Program" icon={Rocket} testid="nav-beta" />
          <Item to="/help" label="Help Center" icon={HelpCircle} testid="nav-help" />
          <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 px-3 pt-4 pb-2">Account</div>
          <Item to="/settings" label="Settings" icon={SettingsIcon} testid="nav-settings" />
          <Item to="/settings/billing" label="Billing" icon={CreditCard} testid="nav-billing" />
          {user?.is_admin && (
            <Item to="/admin" label="Admin" icon={Shield} testid="nav-admin" />
          )}
          <button onClick={()=>setFbOpen(true)} data-testid="send-feedback-btn" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors">
            <MessageSquarePlus className="w-[18px] h-[18px]" /> Send Feedback
          </button>
        </nav>

        <div className="p-3 border-t border-gray-100">
          <div className="flex items-center gap-3 px-3 py-2 mb-1">
            {user?.photo_url ? (
              <img src={user.photo_url} alt={user?.name || "avatar"} data-testid="sidebar-avatar-img" className="w-9 h-9 rounded-full object-cover border border-gray-100" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center text-white font-semibold text-sm" data-testid="sidebar-avatar-fallback">
                {user?.name?.[0]?.toUpperCase() ?? "C"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">{user?.name}</div>
              <div className="text-xs text-gray-500 truncate">{user?.email}</div>
            </div>
            <div className="flex items-center gap-0.5">
              <GlobalSearch />
              <NotificationBell />
            </div>
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
          <div className="mt-2 text-[10px] text-gray-400 text-center px-3" data-testid="sidebar-uphaus">Built by <a href="https://uphauslabs.com" target="_blank" rel="noopener noreferrer" className="font-semibold hover:text-[#007AFF]">UPHAUS Labs</a></div>
        </div>
      </aside>

      <header className="md:hidden sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-gray-100">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#007AFF] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-display font-bold text-lg tracking-tight">CoachIQ</span>
              <span className="text-[9px] font-bold uppercase tracking-widest text-white bg-emerald-500 rounded px-1 py-0.5">Beta</span>
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            <GlobalSearch />
            <NotificationBell />
            <button data-testid="mobile-feedback-btn" onClick={()=>setFbOpen(true)} aria-label="Feedback" className="text-gray-500 hover:text-gray-900 p-2"><MessageSquarePlus className="w-4 h-4" /></button>
            <button data-testid="mobile-logout-btn" onClick={() => { logout(); nav("/"); }} aria-label="Sign out" className="text-gray-500 hover:text-gray-900 p-2"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <main className="md:ml-64 pb-24 md:pb-8">
        {showAnn && (
          <div className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-4 md:px-8 py-2.5" data-testid="announcement-banner">
            <div className="max-w-6xl mx-auto flex items-center gap-3">
              <Megaphone className="w-4 h-4 shrink-0" />
              <div className="text-sm flex-1"><span className="font-semibold uppercase tracking-wide text-white/80 text-xs mr-2">{announcement.kind}</span>{announcement.text}</div>
              <button onClick={dismiss} data-testid="dismiss-ann" className="text-white/70 hover:text-white p-1"><X className="w-4 h-4" /></button>
            </div>
          </div>
        )}
        <div className="max-w-6xl mx-auto p-4 md:p-8 animate-fade-in">
          <TrialBanner />
          <FounderBanner />
          {children}
        </div>
      </main>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-t border-gray-100">
        <div className="grid grid-cols-6 h-16">
          {BOTTOM.map(({ to, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} data-testid={testid} className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${
                isActive ? "text-[#007AFF]" : "text-gray-500"
              }`
            }>
              <Icon className="w-5 h-5" strokeWidth={2} />
              {label}
            </NavLink>
          ))}
          <button data-testid="nav-more-mobile" onClick={()=>setMoreOpen(true)} className="flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-gray-500 hover:text-[#007AFF]">
            <MoreHorizontal className="w-5 h-5" strokeWidth={2} />
            More
          </button>
        </div>
      </nav>

      <MoreSheet open={moreOpen} onClose={()=>setMoreOpen(false)} isAdmin={!!user?.is_admin} />
      <FeedbackDialog open={fbOpen} onClose={()=>setFbOpen(false)} />
    </div>
  );
}
